import { Box, Grid, useMediaQuery } from '@chakra-ui/react';
import { memo, useEffect, useRef, useState } from 'react';

import { Image } from '#features/ui/components/Image';
import { Panel } from '#features/ui/components/Panel';

export const mosaicTileCount = 64;
export const mosaicHeroCount = 80;
export const shuffledHeroes = () => {
  const heroes = Array.from({ length: mosaicHeroCount }, (_, index) => index);
  for (let index = heroes.length - 1; index > 0; index--) {
    const other = Math.floor(Math.random() * (index + 1));
    [heroes[index], heroes[other]] = [heroes[other]!, heroes[index]!];
  }
  return heroes;
};
const initialTiles = (random = false) => {
  const heroes = random ? shuffledHeroes() : Array.from({ length: mosaicHeroCount }, (_, i) => i);
  return Array.from({ length: mosaicTileCount }, (_, index) => ({
    slotId: `auth-tile-${index}`,
    hero: heroes[index]!,
  }));
};
const fallbackAtlas = '/images/auth/hero-atlas-v1.webp';
const preferredAtlas = import.meta.env.AUTH_MOSAIC_ATLAS_URL || fallbackAtlas;
const portraitPosition = (hero: number) =>
  `${((hero % 8) / 7) * 100}% ${(Math.floor(hero / 8) / 9) * 100}%`;

/** 80 bundled portraits share one image request; swapping a tile never fetches another asset. */
export const GameMosaic = ({ compact = false }: { compact?: boolean }) => {
  const [reducedMotion] = useMediaQuery(['(prefers-reduced-motion: reduce)'], {
    ssr: true,
    fallback: [true],
  });
  const [visible, setVisible] = useState(true);
  const [inViewport, setInViewport] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [shuffled, setShuffled] = useState(false);
  const [atlas, setAtlas] = useState<string>(preferredAtlas);
  const [tiles, setTiles] = useState(() => initialTiles());
  const mosaicRef = useRef<HTMLDivElement>(null);
  const ready = loaded && shuffled;

  useEffect(() => {
    setTiles(initialTiles(true));
    setShuffled(true);
  }, []);
  useEffect(() => {
    if (!mosaicRef.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) =>
      setInViewport(entry?.isIntersecting ?? false)
    );
    observer.observe(mosaicRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setVisible(document.visibilityState === 'visible');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  useEffect(() => {
    if (reducedMotion || !visible || !inViewport || !ready) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(
        () => {
          const focused = document.activeElement;
          if (
            !focused?.matches('input, textarea, select') &&
            !focused?.closest('[data-auth-controls]')
          ) {
            setTiles((current) => {
              const index = Math.floor(Math.random() * current.length);
              const used = new Set(current.map((tile) => tile.hero));
              const choices = Array.from({ length: mosaicHeroCount }, (_, hero) => hero).filter(
                (hero) => !used.has(hero)
              );
              const next = [...current];
              next[index] = {
                ...current[index]!,
                hero: choices[Math.floor(Math.random() * choices.length)]!,
              };
              return next;
            });
          }
          schedule();
        },
        (compact ? 1000 : 900) + Math.random() * 500
      );
    };
    schedule();
    return () => clearTimeout(timer);
  }, [reducedMotion, visible, inViewport, ready, compact]);

  return (
    <Grid
      templateColumns="repeat(8, minmax(0, 1fr))"
      gap={{ base: 1, lg: 2 }}
      w="100%"
      aria-hidden="true"
      data-auth-mosaic=""
      ref={mosaicRef}
    >
      <Image
        src={atlas}
        alt=""
        width={640}
        height={800}
        loading="eager"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (atlas !== fallbackAtlas) setAtlas(fallbackAtlas);
        }}
        style={{ display: 'none' }}
      />
      {tiles.map((tile) =>
        ready ? (
          <MosaicTile
            key={tile.slotId}
            hero={tile.hero}
            atlas={atlas}
            reducedMotion={reducedMotion}
          />
        ) : (
          <Panel key={tile.slotId} variant="object" aspectRatio="1" data-game-art={tile.hero} />
        )
      )}
    </Grid>
  );
};

const MosaicTile = memo(function MosaicTile({
  hero,
  atlas,
  reducedMotion,
}: {
  hero: number;
  atlas: string;
  reducedMotion: boolean;
}) {
  const [current, setCurrent] = useState(hero);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    if (current === hero) return;
    if (reducedMotion) {
      setCurrent(hero);
      setFading(false);
      return;
    }
    setFading(true);
  }, [current, hero, reducedMotion]);

  return (
    <Panel
      variant="object"
      aspectRatio="1"
      position="relative"
      overflow="hidden"
      data-game-art={hero}
      backgroundImage={`url('${atlas}')`}
      backgroundSize="800% 1000%"
      backgroundPosition={portraitPosition(current)}
    >
      <Box
        position="absolute"
        inset={0}
        opacity={fading ? 1 : 0}
        backgroundImage={`url('${atlas}')`}
        backgroundSize="800% 1000%"
        backgroundPosition={portraitPosition(hero)}
        transitionProperty={reducedMotion ? 'none' : 'opacity'}
        transitionDuration="slow"
        transitionTimingFunction="var(--chakra-easings-ease-out)"
        onTransitionEnd={() => {
          if (fading) {
            setCurrent(hero);
            setFading(false);
          }
        }}
      />
    </Panel>
  );
});
